import React, { lazy } from 'react';

// project import
import MainLayout from 'layout/MainLayout';
import CommonLayout from 'layout/CommonLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import RouteGuard from 'utils/route-guard/RouteGuard';
import { Navigate } from 'react-router';
import { OrgPermissions, moduleKeys } from 'utils/permissions';
import { devicesTab, userTypes } from 'utils/constants';
import EditRental from '../pages/main-pages/EditRental';
import NewRental from '../pages/main-pages/NewRental';
import { Rentals } from '../pages/main-pages/Rentals';
import TabRentalManagement from '../sections/view-edit-org/TabRentalManagement';
import { WEB_DATA_USE_STANDARD } from 'utils/route-constants';

// pages maintenance pages
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const PlatformMaintenance = Loadable(
    lazy(() => import('pages/maintenance/PlatformMaintenance'))
);

// render - main-pages
const Dashboard = Loadable(lazy(() => import('pages/main-pages/Dashboard')));
// const AccessLog = Loadable(lazy(() => import('pages/main-pages/AccessLog')));
const MyOrganization = Loadable(lazy(() => import('pages/main-pages/MyOrganization')));
const Analysis = Loadable(lazy(() => import('pages/main-pages/Analysis')));
const Devices = Loadable(lazy(() => import('pages/main-pages/Devices')));
const MyNotifications = Loadable(lazy(() => import('pages/main-pages/MyNotifications')));
const GuardReports = Loadable(lazy(() => import('pages/main-pages/GuardReports')));
const TrafficCitations = Loadable(lazy(() => import('pages/main-pages/TrafficCitations')));
// const Clips = Loadable(lazy(() => import('pages/main-pages/Clips')));
const Settings = Loadable(lazy(() => import('pages/main-pages/Settings')));
const Cameras = Loadable(lazy(() => import('pages/main-pages/Cameras')));
const LiveView = Loadable(lazy(() => import('pages/main-pages/LiveView')));
const NewReservation = Loadable(lazy(() => import('pages/main-pages/NewReservation')));
const EditReservation = Loadable(lazy(() => import('pages/main-pages/EditReservation')));
const TenantLookup = Loadable(lazy(() => import('pages/main-pages/TenantLookup')));
const AccessControl = Loadable(lazy(() => import('pages/main-pages/AccessControl')));
const Property = Loadable(lazy(() => import('pages/main-pages/Property')));
const VirtualGuard = Loadable(lazy(() => import('pages/main-pages/VirtualGuard')));
const ManagerAddOtc = Loadable(lazy(() => import('pages/main-pages/ManagerAddOtc')));
const WebPrivacyPolicy = Loadable(
    lazy(() => import('pages/main-pages/WebPrivacyPolicy'))
);
const WebTerms = Loadable(lazy(() => import('pages/main-pages/WebTerms')));
const DataUseStandard = Loadable(lazy(() => import('pages/main-pages/DataUseStandard')));
const SmsConsent = Loadable(lazy(() => import('pages/main-pages/SmsConsent')));
const CaPrivacyPolicy = Loadable(lazy(() => import('pages/main-pages/CaPrivacyPolicy')));
const DoNotSell = Loadable(lazy(() => import('pages/main-pages/DoNotSell')));
const MobileEULA = Loadable(lazy(() => import('pages/main-pages/MobileEULA')));
const MobilePrivacyPolicy = Loadable(
    lazy(() => import('pages/main-pages/MobilePrivacyPolicy'))
);
const AcceptInvite = Loadable(
    lazy(() => import('pages/invitation/accept-invite'))
);
const InvitationConfirmation = Loadable(
    lazy(() => import('pages/invitation/invitation-confirmation'))
);
const InvitationError = Loadable(lazy(() => import('pages/invitation/invitation-error')));
const Queue = Loadable(lazy(() => import('pages/main-pages/Queue')));
const LprViewer = Loadable(lazy(() => import('pages/main-pages/LprViewer')));
const TabInfo = Loadable(lazy(() => import('sections/view-edit-org/TabInfo')));
const TabResidentEmails = Loadable(
    lazy(() => import('sections/view-edit-org/TabResidentEmails'))
);

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    children: [
        {
            path: '*',
            element: <MaintenanceError />
        },
        {
            path: '/',
            element: (
                <AuthGuard>
                    <MainLayout />
                </AuthGuard>
            ),
            children: [
                {
                    path: 'dashboard',
                    element: (
                        <RouteGuard
                            module={moduleKeys.ORG_DASHBOARD}
                            permission={OrgPermissions.ORG_DASHBOARD}
                        >
                            <Dashboard />
                        </RouteGuard>
                    )
                },
                {
                    path: 'access-control',
                    element: (
                        <RouteGuard
                            module={moduleKeys.ORG_DASHBOARD}
                            permission={OrgPermissions.ORG_DASHBOARD}
                        >
                            <Analysis />
                        </RouteGuard>
                    )
                },
                {
                    path: 'administration',
                    element: (
                        <RouteGuard
                            module={moduleKeys.ORG_DASHBOARD}
                            permission={OrgPermissions.ORG_DASHBOARD}
                        >
                            <AccessControl />
                        </RouteGuard>
                    )
                },
                {
                    path: 'administration/add-otc',
                    element: (
                        <RouteGuard
                            module={moduleKeys.ORG_DASHBOARD}
                            permission={OrgPermissions.ORG_DASHBOARD}
                        >
                            <ManagerAddOtc />
                        </RouteGuard>
                    )
                },
                {
                    path: 'administration/property/:id',
                    element: <Property />
                },
                {
                    path: 'my-organization',
                    element: (
                        <RouteGuard
                            module={moduleKeys.ORG_DASHBOARD}
                            permission={OrgPermissions.ORG_DASHBOARD}
                        >
                            <MyOrganization />
                        </RouteGuard>
                    )
                },
                // {
                //     path: 'access-logs',
                //     element: (
                //         <RouteGuard
                //             module={moduleKeys.ORG_DASHBOARD}
                //             permission={OrgPermissions.ORG_DASHBOARD}
                //         >
                //             <Dashboard />
                //         </RouteGuard>
                //     )
                // },
                {
                    path: 'devices',
                    element: (
                        <RouteGuard
                            module={moduleKeys.ORG_DASHBOARD}
                            permission={OrgPermissions.ORG_DASHBOARD}
                            alsoAllow={[
                                {
                                    module: moduleKeys.CREDENTIAL_SHARING_CONSUMER,
                                    permission: OrgPermissions.MANAGE_CREDENTIAL_GRANTS
                                }
                            ]}
                        >
                            <Devices />
                        </RouteGuard>
                    )
                },
                {
                    // A per-user preference, so no module or org permission gate — only the sign-in the whole layout needs.
                    path: 'my-notifications',
                    element: <MyNotifications />
                },
                {
                    path: 'credential-grants',
                    element: (
                        <Navigate
                            to={`/devices?tab=${encodeURIComponent(
                                devicesTab.PENDING_DEVICES
                            )}&source=shared`}
                            replace
                        />
                    )
                },
                {
                    path: 'guard-reports',
                    element: (
                        <RouteGuard
                            module={moduleKeys.GUARD}
                            permission={OrgPermissions.MANAGE_REPORTS}
                            allowUserTypes={[userTypes.SUPERVISOR]}
                        >
                            <GuardReports />
                        </RouteGuard>
                    )
                },
                {
                    path: 'traffic-citations',
                    element: (
                        <RouteGuard
                            module={moduleKeys.TRAFFIC_CITATIONS}
                            permission={OrgPermissions.MANAGE_TRAFFIC_CITATIONS}
                        >
                            <TrafficCitations />
                        </RouteGuard>
                    )
                },
                {
                    path: 'virtual-guard',
                    element: (
                        <RouteGuard
                            module={moduleKeys.VIRTUAL_GUARD}
                            permission={OrgPermissions.VIRTUAL_GUARD}
                        >
                            <VirtualGuard />
                        </RouteGuard>
                    )
                },
                {
                    path: 'queue',
                    element: (
                        <RouteGuard
                            module={moduleKeys.VIRTUAL_GUARD}
                            permission={OrgPermissions.VIRTUAL_GUARD}
                        >
                            <Queue />
                        </RouteGuard>
                    )
                },
                {
                    path: 'live-view',
                    element: (
                        <RouteGuard
                            module={moduleKeys.CLOUD_VIDEO}
                            permission={OrgPermissions.CLOUD_VIDEO}
                        >
                            <LiveView />
                        </RouteGuard>
                    )
                },
                {
                    path: 'cameras',
                    element: (
                        <RouteGuard
                            module={moduleKeys.CLOUD_VIDEO}
                            permission={OrgPermissions.CLOUD_VIDEO}
                        >
                            <Cameras />
                        </RouteGuard>
                    )
                },
                // {
                //     path: 'clips',
                //     element: (
                //         <RouteGuard
                //             module={moduleKeys.CLOUD_VIDEO}
                //             permission={OrgPermissions.CLOUD_VIDEO}
                //         >
                //             <Clips />
                //         </RouteGuard>
                //     )
                // },
                {
                    path: 'tenant-lookup',
                    element: (
                        <RouteGuard
                            module={moduleKeys.RENTAL_MANAGER}
                            permission={OrgPermissions.RENTAL_MANAGER}
                        >
                            <TenantLookup />
                        </RouteGuard>
                    )
                },
                {
                    path: 'rentals',
                    element: (
                        <RouteGuard
                            module={moduleKeys.SHORT_TERM_RENTAL}
                            permission={OrgPermissions.RENTAL_MANAGER}
                        >
                            <Rentals />
                        </RouteGuard>
                    )
                },
                {
                    path: 'rentals/new-rental',
                    element: (
                        <RouteGuard
                            module={moduleKeys.SHORT_TERM_RENTAL}
                            permission={OrgPermissions.RENTAL_MANAGER}
                        >
                            <NewRental />
                        </RouteGuard>
                    )
                },
                {
                    path: 'rentals/:rentalId',
                    element: (
                        <RouteGuard
                            module={moduleKeys.SHORT_TERM_RENTAL}
                            permission={OrgPermissions.RENTAL_MANAGER}
                        >
                            <EditRental />
                        </RouteGuard>
                    )
                },
                {
                    path: 'new-reservation',
                    element: (
                        <RouteGuard
                            module={moduleKeys.RENTAL_MANAGER}
                            permission={OrgPermissions.RENTAL_MANAGER}
                        >
                            <NewReservation />
                        </RouteGuard>
                    )
                },
                {
                    path: 'new-reservation/:reservationId',
                    element: (
                        <RouteGuard
                            module={moduleKeys.RENTAL_MANAGER}
                            permission={OrgPermissions.RENTAL_MANAGER}
                        >
                            <EditReservation />
                        </RouteGuard>
                    )
                },
                {
                    path: 'plate-search',
                    element: (
                        <RouteGuard
                            module={moduleKeys.LPR}
                            permission={OrgPermissions.LPR}
                        >
                            <LprViewer />
                        </RouteGuard>
                    )
                },
                {
                    path: 'settings',
                    element: (
                        <RouteGuard
                            module={moduleKeys.SETTINGS}
                            permission={OrgPermissions.SETTINGS}
                        >
                            <Settings />
                        </RouteGuard>
                    ),
                    children: [
                        {
                            path: 'org-info',
                            element: <TabInfo />
                        },
                        {
                            path: 'resident-emails',
                            element: <TabResidentEmails />
                        },
                        {
                            path: 'rental-management',
                            element: (
                                <RouteGuard
                                    module={moduleKeys.SHORT_TERM_RENTAL}
                                    permission={OrgPermissions.RENTAL_MANAGER}
                                >
                                    <TabRentalManagement />
                                </RouteGuard>
                            )
                        },
                        { path: '*', element: <Navigate to="org-info" replace /> }
                    ]
                }
            ]
        },
        {
            path: '/accept-invite',
            element: <AcceptInvite />
        },
        {
            path: '/invitation-confirmation',
            element: <InvitationConfirmation />
        },
        {
            path: '/invitation-error',
            element: <InvitationError />
        },
        {
            path: '/privacy',
            element: <WebPrivacyPolicy />
        },
        {
            path: '/terms',
            element: <WebTerms />
        },
        {
            path: WEB_DATA_USE_STANDARD,
            element: <DataUseStandard />
        },
        {
            path: '/sms-consent',
            element: <SmsConsent />
        },
        {
            path: '/ca-privacy',
            element: <CaPrivacyPolicy />
        },
        {
            path: '/mobile-app-eula',
            element: <MobileEULA />
        },
        {
            path: '/mobile-app-privacy',
            element: <MobilePrivacyPolicy />
        },
        {
            path: '/do-not-sell',
            element: <DoNotSell />
        },
        {
            path: '/maintenance',
            element: <CommonLayout />,
            children: [
                {
                    path: '404',
                    element: <MaintenanceError />
                },
                {
                    path: '500',
                    element: <MaintenanceError500 />
                },
                {
                    path: 'platform',
                    element: <PlatformMaintenance />
                }
            ]
        }
    ]
};

export default MainRoutes;
